import { useEffect, useState } from 'react';
import { useDragToUpload } from './use-drag-to-upload';

type Options = {
  /** False while something else owns the page (another dialog, an upload). */
  enabled: boolean;
  /** The dataset dialog is already open; the dashboard takes drops itself. */
  modalOpen: boolean;
  openModal: () => void;
  addFiles: (files: File[]) => void;
};

/**
 * Files dragged in from outside the browser open the dataset dialog with those
 * files already queued, so the customer does not have to open it first and drag
 * again. Files are queued once the dialog is open, so any message about a file
 * that is not allowed is shown by the dashboard instead of being lost.
 */
export function useDatasetFileDrop({
  enabled,
  modalOpen,
  openModal,
  addFiles
}: Options) {
  const { isDragging, droppedFiles, clearDroppedFiles } = useDragToUpload(
    enabled && !modalOpen
  );
  const [pending, setPending] = useState<File[]>([]);

  // A file dropped anywhere the page does not handle makes the browser open it
  // and leave the page, losing the queued files and any upload in progress. This
  // covers the dialog being open (the dashboard handles drops on itself only),
  // another dialog being open, and an upload running.
  useEffect(() => {
    const isFileDrag = (e: DragEvent) =>
      !!e.dataTransfer?.types &&
      Array.from(e.dataTransfer.types).includes('Files');
    const swallow = (e: DragEvent) => {
      if (isFileDrag(e)) e.preventDefault();
    };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  useEffect(() => {
    if (!droppedFiles.length) return;
    setPending(droppedFiles);
    clearDroppedFiles();
    openModal();
  }, [droppedFiles]);

  useEffect(() => {
    if (!modalOpen || !pending.length) return;
    addFiles(pending);
    setPending([]);
  }, [modalOpen, pending]);

  return { isDragging: isDragging && !modalOpen };
}
